import { CSSProperties } from 'react';
import { WEDGE_NOTICE, WEDGE_SHELL } from '@territory/tokens';

/**
 * Shared RepVantage brand primitives for every pre-auth / splash surface
 * (sign-in, magic-redeem, access-denied, code-redeem, request-access, copy-code,
 * PIN login/setup, loaders). Single source of truth so all the doors into PREP
 * look uniform and on-brand.
 *
 * Brand canon (repvantage-brand-identity v3):
 *   · Background  · Deep Field #0A1A10 (green-black, never pure black)
 *   · Accent      · Emerald #34d399 (primary), Amber #F59E0B (VANTAGE / YC)
 *   · Text        · Slate scale #E2E8F0 / #94A3B8 / #64748B / #475569
 *   · Type        · Orbitron (wordmark) · Jost (body) · Share Tech Mono (status)
 *   · Glass       · green-tinted rgba(52,211,153,a), never white-tinted
 *
 * Visual-only. Nothing here touches auth handlers, state, or navigation.
 */

export const BRAND = WEDGE_SHELL;

/** Full-screen container for a standalone splash surface. */
export const brandScreen: CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '32px 20px',
  background: BRAND.screenBg,
  color: BRAND.text,
  fontFamily: BRAND.fontBody,
  boxSizing: 'border-box',
  textAlign: 'center',
};

/**
 * Keyframes + hover/focus rules shared by every standalone surface. Drop one
 * <BrandStyleTag /> per full-screen surface (the embedded forms inherit the
 * rules from their host shell's style tag, which carries the same classes).
 */
export function BrandStyleTag() {
  return (
    <style>{`
      @keyframes wedgeFadeUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
      @keyframes wedgePulse { 0%,100% { opacity: 0.85; transform: scale(1); } 50% { opacity: 0.35; transform: scale(1.06); } }
      @keyframes wedgeRadarSweep { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      @keyframes wedgeLiveDot { 0%,100% { opacity: 1; } 50% { opacity: 0.3; } }
      @media (prefers-reduced-motion: reduce) { [data-wedge-animated] { animation: none !important; } }
    `}</style>
  );
}

/** Animated concentric-radar brand mark, matching the sign-in hero. */
export function BrandMark({ size = 76 }: { size?: number }) {
  const ring = Math.round(size * 0.84);
  return (
    <div
      data-wedge-animated
      style={{
        position: 'relative',
        width: size,
        height: size,
        margin: '0 auto 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: -8,
          borderRadius: '50%',
          border: '1.5px solid rgba(52,211,153,0.40)',
          animation: 'wedgePulse 2.4s ease-in-out infinite',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: '50%',
          overflow: 'hidden',
          pointerEvents: 'none',
          maskImage: 'radial-gradient(circle, #000 62%, transparent 64%)',
          WebkitMaskImage: 'radial-gradient(circle, #000 62%, transparent 64%)',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: '-25%',
            background:
              'conic-gradient(from 0deg, rgba(52,211,153,0) 0deg, rgba(52,211,153,0) 250deg, rgba(52,211,153,0.10) 320deg, rgba(52,211,153,0.55) 358deg, rgba(52,211,153,0) 360deg)',
            animation: 'wedgeRadarSweep 3.6s linear infinite',
          }}
        />
      </div>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: '50%',
          background:
            'radial-gradient(circle at 38% 32%, rgba(52,211,153,0.28), rgba(10,26,16,0) 72%)',
          boxShadow:
            '0 0 0 1px rgba(52,211,153,0.28), inset 0 0 24px rgba(52,211,153,0.10), 0 0 56px rgba(52,211,153,0.22)',
        }}
      />
      <img
        src="/wedge/repvantage-radar.svg?v=20260712"
        alt="RepVantage"
        width={ring}
        height={ring}
        style={{ position: 'relative', zIndex: 1, filter: 'drop-shadow(0 2px 10px rgba(52,211,153,0.30))' }}
      />
    </div>
  );
}

/** REP (theme-light) + VANTAGE (amber) wordmark in Orbitron 800. */
export function BrandWordmark({ size = 20 }: { size?: number }) {
  return (
    <div
      style={{
        fontFamily: BRAND.fontDisplay,
        fontSize: size,
        fontWeight: 800,
        letterSpacing: 4,
        textTransform: 'uppercase',
        marginBottom: 7,
        textAlign: 'center',
      }}
    >
      <span style={{ color: WEDGE_NOTICE.wordmark }}>REP</span>
      <span style={{ color: BRAND.amber }}>VANTAGE</span>
    </div>
  );
}
